import React from 'react'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Hits, NoHits, PageSizeSelector, Pagination, SearchkitComponent, SearchkitManager, SearchkitProvider, Select } from 'searchkit'
import Spinner from '../global/Spinner'
import ProductLandingTwo from './template/ProductLandingTwo'
import PromotionFilters from './PromotionFilters'
import PromotionResultsBar from './PromotionResultsBar'
import { getSearchkitTranslations } from '../catalog/searchkitTranslations'
import { getToken } from '../../store/login/reducers'
import { getSelectedCenter } from '../../store/centers/reducers'

const HITS_PER_PAGE = 24

/**
 * Spinner mientras SearchKit tiene una búsqueda en vuelo (al filtrar, ordenar o paginar).
 * SearchkitComponent se re-renderiza con cada evento del manager, y loading se pone a true
 * antes de lanzar la petición y a false al recibir la respuesta.
 */
class PromotionSearchSpinner extends SearchkitComponent {
    render() {
        const loading = !!(this.searchkit && this.searchkit.loading)
        if (!loading) return null

        // Contenedor absoluto para que el spinner flote sobre el grid sin empujar los productos.
        return (
            <div className="promotion-landing-spinner">
                <Spinner isLoading relative />
            </div>
        )
    }
}

const getSk = (promotionId, centerId, token, locale) => {
    const uri = `/promotions/search/${promotionId}?centerId=${centerId}&lang=${locale}`
    const httpHeaders = token ? { Authorization: 'Bearer ' + token } : {}

    const sk = new SearchkitManager(process.env.REACT_APP_API_HOST + uri, {
        searchUrlPath: '',
        timeout: 60000,
        httpHeaders
    })

    // Mismo diccionario que el catálogo: incluye la traducción del valor de los filtros seleccionados
    // (por ejemplo: Finish: MAT → Matte), que es lo que se ve en el tag del filtro.
    const translations = getSearchkitTranslations()
    sk.translateFunction = key => translations[key]

    return sk
}

/**
 * Listado de productos de la landing de campañas (plantilla 2), con filtros y paginación.
 *
 * A diferencia del resto de la landing, que se sirve de /promotions/data/{id}, este listado va contra
 * /promotions/search/{id}, que devuelve una respuesta con forma de Elastic para que SearchKit pueda
 * usar los mismos filtros y la misma paginación que el catálogo. El backend mantiene el orden de
 * promotion_material y expande los materiales que están en la campaña como normal y como outlet.
 */
class PromotionProductsSearch extends React.Component {
    constructor(props) {
        super(props)
        this.state = { sk: null, pageScope: 4 }
    }

    componentDidMount() {
        window.addEventListener('resize', this.resize)
        this.resize()
        this.buildSk()
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.promotionId !== this.props.promotionId ||
            prevProps.centerId !== this.props.centerId ||
            prevProps.token !== this.props.token ||
            prevProps.intl.locale !== this.props.intl.locale
        ) {
            this.buildSk()
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resize)
    }

    resize = () => {
        this.setState({ pageScope: window.innerWidth < 768 ? 1 : 4 })
    }

    buildSk = () => {
        const { promotionId, centerId, token, intl } = this.props

        if (!promotionId) return

        this.setState({ sk: getSk(promotionId, centerId, token, intl.locale) })
    }

    hitComponent = ({ result }) => (
        <ProductLandingTwo product={result._source} isSellingOutlet={result._source.isSellingOutlet} promotionId={this.props.promotionId} />
    )

    render() {
        if (!this.state.sk) return null

        return (
            <SearchkitProvider searchkit={this.state.sk}>
                <div>
                    <PromotionFilters />
                    <PromotionResultsBar />
                    {/* Wrapper relativo para que el spinner se posicione sobre el grid sin ocupar sitio. */}
                    <div className="promotion-landing-results">
                        <PromotionSearchSpinner />
                        <Hits hitsPerPage={HITS_PER_PAGE} itemComponent={this.hitComponent} className="products-promotion-landing-two" />
                        <NoHits
                            translations={{
                                'NoHits.NoResultsFound': this.props.intl.formatMessage({ id: 'no_results_found' }),
                                'NoHits.SearchWithoutFilters': this.props.intl.formatMessage({ id: 'search_for__without_filters' })
                            }}
                        />
                    </div>
                    <div className="results">
                        <div
                            style={{
                                display: 'flex',
                                columnGap: '24px',
                                justifyContent: 'center',
                                alignItems: 'baseline'
                            }}
                        >
                            <div className="results-pagination" data-cy="promotion__pagination_landing" onClick={() => window.scrollTo(0, 160)}>
                                <Pagination showNumbers={true} pageScope={this.state.pageScope} />
                            </div>
                            <div>
                                <PageSizeSelector className="sk-select-v2" options={[24, 50, 100, 500]} listComponent={Select} />
                            </div>
                        </div>
                    </div>
                </div>
            </SearchkitProvider>
        )
    }
}

const mapStateToProps = state => {
    return {
        token: getToken(state),
        centerId: getSelectedCenter(state)
    }
}

export default injectIntl(connect(mapStateToProps)(PromotionProductsSearch))
